/** biome-ignore-all lint/plugin: app chrome — fixed positioning and state-driven collapse widths cannot move to the theme */
'use client'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MenuIcon from '@mui/icons-material/Menu'
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Image from 'next/image'
import React from 'react'
import useAuth from '../hooks/useAuth'
import SidebarItems from './SidebarItems'

// Use string path instead of static import to avoid Sharp issues during Docker build
const Logo = '/assets/images/WH-logo.png'

interface SidebarProps {
  isCollapsed: boolean
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, setIsCollapsed }) => {
  const theme = useTheme()
  const { user, isLoading } = useAuth()
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Load initial collapsed state from localStorage
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem('main-sidebar-collapsed')
      if (saved !== null) {
        setIsCollapsed(JSON.parse(saved))
      }
    } catch (error: any) {
      console.warn('Failed to load main sidebar state:', error)
    }
  }, [setIsCollapsed])

  // Keep sidebar visible while auth context resolves partner type;
  // only block when loading is complete and user is still missing.
  if (!isLoading && !user) return null

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev)
  }

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const newValue = !prev
      // Save to localStorage for AdminSidebar to read
      try {
        localStorage.setItem('main-sidebar-collapsed', JSON.stringify(newValue))
      } catch (error: any) {
        console.warn('Failed to save main sidebar state:', error)
      }
      return newValue
    })
  }

  const handleCloseOnClick = () => {
    if (isMobile) {
      setIsDrawerOpen(false)
    }
  }

  const sidebarContent = (
    <Box
      sx={{
        padding: 1,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: isCollapsed ? 80 : 250,
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
        borderRight: `1px solid ${theme.palette.divider}`,
        transition: 'width 0.3s ease',
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          padding: 1,
        }}
      >
        {!isCollapsed && (
          <Image src={Logo} alt="logo" width={150} height={25} />
        )}
      </Stack>

      {/* Sidebar Items */}
      <SidebarItems
        isCollapsed={isCollapsed}
        onItemClick={handleCloseOnClick}
      />

      {/* Collapse/Expand Toggle */}
      {!isMobile && (
        <Box
          onClick={toggleCollapse}
          sx={{
            position: 'absolute',
            top: '50%',
            left: isCollapsed ? '80px' : '250px',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '100px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            zIndex: 1200,
          }}
        >
          {isCollapsed ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ChevronLeftIcon fontSize="small" />
          )}
        </Box>
      )}
    </Box>
  )

  return (
    <>
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1200,
            padding: 1,
          }}
        >
          <IconButton edge="start" onClick={toggleDrawer}>
            {isDrawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
      )}

      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={toggleDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: '250px',
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {!isMobile && sidebarContent}
    </>
  )
}

export default Sidebar
